'use client';
import { getEventsByLocation } from '@v2/services/get-events-by-location';
import { getCityLatLonByName } from '@v2/services/getCityLatLonByName';
import { getEventsByCategory } from '@v2/services/getEventsByCategory';
import { AWSLocationResponse, EventResponse, TeamProps } from '@v2/types';
import { getCityPageData } from '@v2/views/city/services/getCityPageData';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EventsList } from '../components/EventsList/EventsList';
import { EventsListSkeleton } from '../components/EventsList/EventsListSkeleton';
import { EventResultsUiConfig } from '../constants/eventPageUiTypes';
import { HomeAwayEventsTypes } from '../components/HomeAwayEventsTab';
import { ShowMoreButton } from '../components/ShowMoreButton';
import { CONCERTS_ID, shouldHideLeagueEvent } from '../util';
import { EventsFilters } from './EventsFilters';
import {
  buildAllLocationsHeaderText,
  getTargetVisibleEventCount,
  shouldContinueFetchingVisibleEvents,
} from './listLeagueEventsHelpers';

interface Props {
  leagueName: string;
  leagueNames?: string[];
  teamSlug?: string;
  withTitle?: boolean;
  /** League landing: large first page, hide Show more (see leagueLandingConfig.eventsList) */
  landingFullListMode?: boolean;
  /** perPage for category + location when landingFullListMode (default 100) */
  landingLargePerPage?: number;
  /** Overrides "Upcoming Games" when set and not concerts */
  upcomingGamesTitleOverride?: string;
  eventResultsConfig?: EventResultsUiConfig;
  initialEvents?: EventResponse[];
  initialTotalEntries?: number;
}

const DEFAULT_LANDING_LARGE_PER_PAGE = 100;
const ALL_LOCATIONS_FETCH_BATCH_SIZE = 20;

const HomeAwayEventstMapping = {
  [HomeAwayEventsTypes.ALL]: undefined,
  [HomeAwayEventsTypes.HOME]: true,
  [HomeAwayEventsTypes.AWAY]: false,
};

export const ListLeagueEventsWrapper = ({
  leagueName,
  leagueNames,
  teamSlug,
  withTitle = true,
  landingFullListMode = false,
  landingLargePerPage = DEFAULT_LANDING_LARGE_PER_PAGE,
  upcomingGamesTitleOverride,
  eventResultsConfig,
  initialEvents,
  initialTotalEntries = 0,
}: Props) => {
  const hasSeededInitialEvents =
    Array.isArray(initialEvents) && initialEvents.length > 0;
  const [events, setEvents] = useState<EventResponse[] | undefined>(
    hasSeededInitialEvents ? initialEvents : undefined
  );
  const [allEventsPage, setAllEventsPage] = useState(1);
  const [eventsByLocationPage, setEventsByLocationPage] = useState(1);
  const [allEventsTotalEntries, setAllEventsTotalEntries] =
    useState(initialTotalEntries);
  const [hasMoreAllEvents, setHasMoreAllEvents] = useState(
    initialTotalEntries > (initialEvents?.length ?? 0)
  );
  const [eventsByLocationTotalEntries, setEventsByLocationTotalEntries] =
    useState(0);
  const [isLocationChangeLoading, setIsLocationChangeLoading] = useState(false);
  const searchParams = useSearchParams();
  const { data, status } = useSession();

  const defaultUserLocation = data?.user?.location;

  const [location, setLocation] = useState<string>(
    defaultUserLocation || 'Miami, FL'
  );
  const [eventsByLocation, setEventsByLocation] = useState<any>([]);
  const [isLoadingEventsByLocation, setIsLoadingEventsByLocation] =
    useState(false);

  const [isLoadingEventsByCategory, setIsLoadingEventsByCategory] = useState(
    !hasSeededInitialEvents
  );
  const allEventsRequestIdRef = useRef(0);
  // Empty arrays are truthy via Boolean([]) — only skip client fetch when SSR actually seeded rows.
  const shouldUseInitialEvents = useRef(hasSeededInitialEvents);

  const [startDateFilter, setStartDateFilter] = useState<string>();
  const [endDateFilter, setEndDateFilter] = useState<string>();

  const [homeAwayFilter, setHomeAwayFilter] = useState<HomeAwayEventsTypes>();
  const [opponentId, setOpponentId] = useState<number>();

  const parentId = searchParams?.get('parentId');
  const opponentIdParam = searchParams?.get('opponentId');

  const isConcerts = CONCERTS_ID.includes(Number(parentId));
  const categoryNames = leagueNames?.length ? leagueNames : [leagueName];
  const hideNearbyEvents = eventResultsConfig?.hideNearbyEvents ?? false;
  const showAllEventsByDefault =
    eventResultsConfig?.showAllEventsByDefault ?? false;

  const handleGetLocation = async (loc: string) => {
    const cityData = await getCityPageData(loc);

    if (cityData) {
      setLocation(`${cityData.Municipality},`);
    }
  };

  useEffect(() => {
    if (defaultUserLocation) {
      handleGetLocation(defaultUserLocation);
    }
  }, [defaultUserLocation]);

  useEffect(() => {
    if (opponentIdParam) {
      const parsed = Number(opponentIdParam);
      if (!Number.isNaN(parsed)) {
        setOpponentId(parsed);
      }
    }
  }, [opponentIdParam]);

  useEffect(() => {
    if (shouldUseInitialEvents.current) {
      shouldUseInitialEvents.current = false;
      return;
    }

    const requestId = ++allEventsRequestIdRef.current;

    const fetchAllEvents = async () => {
      setIsLoadingEventsByCategory(true);

      try {
        const targetVisibleEvents = getTargetVisibleEventCount({
          page: allEventsPage,
          config: eventResultsConfig,
        });
        let currentPage = 1;
        let totalEntries = 0;
        let bufferedVisibleEvents: EventResponse[] = [];
        let hasMorePages = true;

        while (
          shouldContinueFetchingVisibleEvents({
            visibleCount: bufferedVisibleEvents.length,
            targetVisibleCount: targetVisibleEvents,
            hasMorePages,
          })
        ) {
          const categoryEvents = await Promise.all(
            categoryNames.map((categoryName) =>
              getEventsByCategory({
                categoryName,
                serverSide: false,
                page: currentPage,
                startDate: startDateFilter,
                endDate: endDateFilter,
                teamSlug,
                onlyHomeGames:
                  HomeAwayEventstMapping[
                    homeAwayFilter ?? HomeAwayEventsTypes.ALL
                  ],
                opponentId,
                perPage: ALL_LOCATIONS_FETCH_BATCH_SIZE,
                concerts: isConcerts,
              })
            )
          );

          if (requestId !== allEventsRequestIdRef.current) {
            return;
          }

          const pageEvents = categoryEvents
            .flatMap((data) => data?.events ?? [])
            .sort(
              (left, right) =>
                new Date(left.occurs_at_local).getTime() -
                new Date(right.occurs_at_local).getTime()
            );
          totalEntries = categoryEvents.reduce(
            (total, data) => total + (data?.total_entries ?? 0),
            0
          );
          const filteredPageEvents = pageEvents.filter(
            (event) => !shouldHideLeagueEvent(event.name)
          );

          bufferedVisibleEvents =
            bufferedVisibleEvents.concat(filteredPageEvents);

          hasMorePages = categoryEvents.some(
            (data) =>
              currentPage * ALL_LOCATIONS_FETCH_BATCH_SIZE <
              (data?.total_entries ?? 0)
          );
          currentPage += 1;
        }

        if (requestId !== allEventsRequestIdRef.current) {
          return;
        }

        setAllEventsTotalEntries(totalEntries);
        setEvents(bufferedVisibleEvents.slice(0, targetVisibleEvents));
        setHasMoreAllEvents(
          bufferedVisibleEvents.length > targetVisibleEvents || hasMorePages
        );
      } catch (e) {
        console.error(e);

        if (requestId !== allEventsRequestIdRef.current) {
          return;
        }

        setAllEventsTotalEntries(0);
        setEvents([]);
        setHasMoreAllEvents(false);
      } finally {
        if (requestId === allEventsRequestIdRef.current) {
          setIsLoadingEventsByCategory(false);
        }
      }
    };

    fetchAllEvents();
  }, [
    allEventsPage,
    startDateFilter,
    endDateFilter,
    teamSlug,
    homeAwayFilter,
    opponentId,
    leagueName,
    leagueNames,
    isConcerts,
  ]);

  useEffect(() => {
    if (status === 'loading' || hideNearbyEvents) {
      return;
    }
    async function fetchEventsByLocation() {
      setIsLoadingEventsByLocation(true);

      try {
        const cityData = await getCityLatLonByName(location);

        if (!cityData) return;
        const response = await getEventsByLocation({
          serverSide: false,
          lat: cityData?.Geometry.lat,
          lon: cityData?.Geometry.lon,
          categoryName: leagueName,
          withVenueImages: false,
          perPage: landingFullListMode ? landingLargePerPage : 3,
          page: eventsByLocationPage,
          sports: !isConcerts,
          concerts: isConcerts,
          startDate: startDateFilter,
          endDate: endDateFilter,
          teamSlug,
          onlyHomeGames:
            HomeAwayEventstMapping[homeAwayFilter ?? HomeAwayEventsTypes.ALL],
          opponentId,
          league: leagueName,
        });
        setEventsByLocationTotalEntries(response.totalEvents);
        setEventsByLocation((old) => {
          // Only append events when paginating (page > 1), otherwise replace
          if (eventsByLocationPage > 1 && old?.length) {
            return [...old, ...response.events];
          }
          return response.events;
        });
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoadingEventsByLocation(false);
        setIsLocationChangeLoading(false);
      }
    }

    fetchEventsByLocation();
  }, [
    data,
    location,
    leagueName,
    eventsByLocationPage,
    startDateFilter,
    endDateFilter,
    teamSlug,
    homeAwayFilter,
    opponentId,
    landingFullListMode,
    landingLargePerPage,
    hideNearbyEvents,
  ]);

  const onLocationChange = useCallback((location: AWSLocationResponse) => {
    const locationText = `${location.Text} ${location?.Region ?? ''}`;
    setLocation(locationText);
    setEventsByLocation([]);
    setEventsByLocationTotalEntries(0);
    setIsLocationChangeLoading(true);
    setAllEventsPage(1);
    setEventsByLocationPage(1);
  }, []);

  const onDateChange = useCallback(
    (startDate: string, endDate: string) => {
      const isAnyDate = !endDate;
      const isPreviousFilter =
        (isAnyDate && !endDateFilter?.length) || endDateFilter === endDate;
      if (isPreviousFilter) {
        return;
      }
      setStartDateFilter(isAnyDate ? undefined : (startDate ?? undefined));
      setEndDateFilter(endDate?.length ? endDate : undefined);
      setEventsByLocation([]);
      setEvents([]);
      setAllEventsPage(1);
      setEventsByLocationPage(1);
      setIsLocationChangeLoading(true);
      setIsLoadingEventsByCategory(true);
    },
    [endDateFilter]
  );

  const onHomeAwayChange = useCallback((tab: HomeAwayEventsTypes) => {
    setAllEventsPage(1);
    setEventsByLocationPage(1);
    setHomeAwayFilter(tab);
    setEventsByLocation([]);
    setIsLocationChangeLoading(true);
    setIsLoadingEventsByCategory(true);
  }, []);

  const onOpponentChange = useCallback((opponent?: TeamProps) => {
    setAllEventsPage(1);
    setEventsByLocationPage(1);
    setOpponentId(opponent?.teamId);
    setIsLocationChangeLoading(true);
    setIsLoadingEventsByCategory(true);
  }, []);

  const visibleAllEventsCount = events?.length ?? 0;
  const allLocationsHeaderText = buildAllLocationsHeaderText({
    visibleCount: visibleAllEventsCount,
    totalEntries: allEventsTotalEntries,
    config: eventResultsConfig,
  });

  return (
    <div>
      <div>
        {withTitle && (
          <h2 className="mb-6 text-2xl font-bold">
            {isConcerts
              ? 'Upcoming Concerts'
              : (upcomingGamesTitleOverride ?? 'Upcoming Games')}
          </h2>
        )}
        <EventsFilters
          onLocationChange={onLocationChange}
          onDateChange={onDateChange}
          isTeamPage={Boolean(teamSlug)}
          leagueSlug={leagueName}
          teamSlug={teamSlug}
          onHomeAwayChange={onHomeAwayChange}
          onOpponentChange={onOpponentChange}
          eventResultsConfig={eventResultsConfig}
        />
      </div>
      <div className="flex flex-col gap-y-5">
        {!hideNearbyEvents && (
          <div>
            {isLocationChangeLoading ? (
              <EventsListSkeleton numberOfLines={3} />
            ) : (
              <>
                <EventsList
                  className="mt-5"
                  identifier="eventsByLocation"
                  headerText={`${eventsByLocationTotalEntries} events near ${location}`}
                  events={eventsByLocation}
                  itemListName="nearby_events"
                  itemCategory={leagueName}
                />
                {!landingFullListMode &&
                  Boolean(
                    eventsByLocation?.length < eventsByLocationTotalEntries &&
                    eventsByLocationTotalEntries
                  ) && (
                    <ShowMoreButton
                      isLoading={isLoadingEventsByLocation}
                      onShowMoreClick={() => {
                        setEventsByLocationPage((old) => old + 1);
                      }}
                    />
                  )}
              </>
            )}
          </div>
        )}

        <div>
          {isLoadingEventsByCategory ? (
            <EventsListSkeleton numberOfLines={15} />
          ) : (
            <>
              <EventsList
                identifier="allEvents"
                headerText={allLocationsHeaderText}
                events={events ?? []}
                shouldReturnEmpty={false}
                actionLabel={eventResultsConfig?.actionLabel}
                layout={eventResultsConfig?.layout}
                itemListName={
                  teamSlug ? 'team_upcoming_events' : 'league_all_events'
                }
                itemCategory={leagueName}
              />
              {Boolean(hasMoreAllEvents) && !showAllEventsByDefault && (
                <ShowMoreButton
                  isLoading={isLoadingEventsByCategory}
                  onShowMoreClick={() => {
                    setAllEventsPage((old) => old + 1);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
